import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, streamConcept, API } from "../lib/api";
import { ConceptMemo } from "../components/ConceptMemo";
import { Sparkles, Loader2, FlaskConical, Clock, Rocket, CheckCircle2, FileText, RefreshCw, Check } from "lucide-react";
import { CONCEPT_ARCHETYPES, CONCEPT_CATEGORIES, CONCEPT_GEOGRAPHIES } from "../lib/conceptOptions";

const BACKEND = API.replace(/\/api$/, "");
const STAGES = ["READING SIGNAL CORPUS", "POSITIONING + ONE-LINE PITCH", "FORMAT ENGINEERING", "INGREDIENT STACK", "REAL-LIFE SCENARIOS", "COMPOSING MEMO"];

function MemoProgress({ chars }) {
  const stage = Math.min(Math.floor(chars / 420), STAGES.length - 1);
  const pct = Math.min(95, Math.round((chars / 2800) * 100));
  return (
    <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full py-8" data-testid="memo-progress">
      <div className="space-y-3">
        {STAGES.map((s, i) => (
          <div key={s} className={`flex items-center gap-3 font-mono text-xs transition-opacity ${i > stage ? "opacity-30" : ""}`}>
            {i < stage ? <Check className="w-3.5 h-3.5 text-bm-green" />
              : i === stage ? <span className="w-3.5 h-3.5 flex items-center justify-center"><span className="w-2 h-2 bg-bm-amber animate-pulse" /></span>
              : <span className="w-3.5 h-3.5 border border-white/20" />}
            <span className={i === stage ? "text-bm-amber" : i < stage ? "text-bm-green" : "text-bm-dim"}>{s}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 h-1 bg-white/10 overflow-hidden">
        <div className="h-full bg-bm-amber transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="font-mono text-[10px] text-bm-dim mt-2">{chars.toLocaleString()} tokens streamed · claude-fable-5</div>
    </div>
  );
}

export default function Concepts() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const projectSlugParam = params.get("project");

  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    project_id: projectSlugParam || "",
    category: "",
    geography_inspiration: "",
    target_archetype: "",
    free_prompt: "",
  });
  const [sessionId] = useState(() => `cgen_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const [streaming, setStreaming] = useState(false);
  const [chars, setChars] = useState(0);
  const [memo, setMemo] = useState(null);
  const [memoCreatedAt, setMemoCreatedAt] = useState(null);
  const [legacyText, setLegacyText] = useState("");
  const [errorMsg, setErrorMsg] = useState(null);
  const [packshotUrl, setPackshotUrl] = useState(null);
  const [packshotLoading, setPackshotLoading] = useState(false);
  const [memoizing, setMemoizing] = useState(false);
  const [activeConceptId, setActiveConceptId] = useState(null);
  const [history, setHistory] = useState([]);
  const [promoting, setPromoting] = useState(false);
  const [promoteResult, setPromoteResult] = useState(null);

  useEffect(() => {
    api.projects().then((d) => setProjects(d.projects));
    api.listConcepts().then((d) => setHistory(d.concepts || []));
  }, []);

  useEffect(() => {
    if (projectSlugParam) setForm((f) => ({ ...f, project_id: projectSlugParam }));
  }, [projectSlugParam]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const refreshHistory = async () => {
    const h = await api.listConcepts();
    setHistory(h.concepts || []);
    return h.concepts || [];
  };

  const triggerPackshot = async (conceptId) => {
    setPackshotLoading(true);
    try {
      const r = await api.generatePackshot(conceptId);
      setPackshotUrl(r.url ? `${BACKEND}${r.url}` : null);
      refreshHistory();
    } catch (e) {
      console.debug("Packshot generation failed:", e?.message);
    } finally {
      setPackshotLoading(false);
    }
  };

  const generate = async () => {
    if (streaming) return;
    setMemo(null); setLegacyText(""); setErrorMsg(null); setChars(0);
    setPackshotUrl(null); setActiveConceptId(null); setPromoteResult(null);
    setMemoCreatedAt(null);
    setStreaming(true);
    let newId = null;
    try {
      const body = {
        session_id: sessionId,
        project_id: form.project_id || null,
        category: form.category || null,
        geography_inspiration: form.geography_inspiration || null,
        target_archetype: form.target_archetype || null,
        free_prompt: form.free_prompt || null,
      };
      for await (const ev of streamConcept(body)) {
        if (ev.type === "delta") setChars((c) => c + (ev.content?.length || 0));
        else if (ev.type === "memo") { setMemo(ev.memo); setMemoCreatedAt(new Date().toISOString()); newId = ev.concept_id; setActiveConceptId(ev.concept_id); }
        else if (ev.type === "memo_error") { setLegacyText(ev.raw || ""); newId = ev.concept_id; setActiveConceptId(ev.concept_id); }
        else if (ev.type === "error") setErrorMsg(ev.content);
      }
      refreshHistory();
    } catch (e) {
      setErrorMsg(e.message || "Concept generation failed — check connection and retry.");
    } finally {
      setStreaming(false);
    }
    if (newId) triggerPackshot(newId);
  };

  const promote = async () => {
    if (!activeConceptId || promoting) return;
    setPromoting(true);
    setPromoteResult(null);
    try {
      const r = await api.promoteConcept(activeConceptId);
      setPromoteResult(r);
      refreshHistory();
    } catch (e) {
      setPromoteResult({ error: e.response?.data?.detail || e.message || "Promotion failed" });
    } finally {
      setPromoting(false);
    }
  };

  const rebuildAsMemo = async () => {
    if (!activeConceptId || memoizing) return;
    setMemoizing(true);
    try {
      const r = await api.memoizeConcept(activeConceptId);
      setMemo(r.memo);
      setLegacyText("");
      refreshHistory();
      triggerPackshot(activeConceptId);
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Memo conversion failed");
    } finally {
      setMemoizing(false);
    }
  };

  const loadHistory = (c) => {
    setActiveConceptId(c.id);
    setErrorMsg(null);
    setMemoCreatedAt(c.created_at);
    setPromoteResult(c.promoted_opportunity_id ? { opportunity_id: c.promoted_opportunity_id, already_promoted: true } : null);
    if (c.memo) {
      setMemo(c.memo);
      setLegacyText("");
      setPackshotUrl(c.packshot_url ? `${BACKEND}${c.packshot_url}` : null);
      if (!c.packshot_url) triggerPackshot(c.id);
    } else {
      setMemo(null);
      setPackshotUrl(null);
      setLegacyText(c.concept_text || "");
    }
  };

  const hasOutput = memo || legacyText || streaming;

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4">
        <div className="label-mono text-bm-amber">CONCEPT GENERATOR</div>
        <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
          What we should build next.
        </h1>
        <p className="text-sm text-bm-muted mt-2 max-w-3xl">
          Claude-generated concepts rendered as a one-page infographic memo — packshot, pitch, format, ingredients, real-life scenarios. One-click promotion to a draft Opportunity with conviction scoring.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Form */}
        <div className="bm-card p-5 space-y-3 lg:col-span-1 self-start" data-testid="concept-form">
          <div className="label-mono text-bm-cyan mb-2">INPUTS</div>

          <div>
            <label className="label-mono text-bm-dim">House</label>
            <select value={form.project_id} onChange={(e) => set("project_id", e.target.value)}
              data-testid="concept-project" className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none">
              <option value="">— Any (free-form) —</option>
              {projects.map((p) => <option key={p.id} value={p.slug}>{`${p.name} · ${p.status}`}</option>)}
            </select>
          </div>

          <div>
            <label className="label-mono text-bm-dim">Category</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)}
              data-testid="concept-category" className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none">
              {CONCEPT_CATEGORIES.map(c => <option key={c || "any"} value={c}>{c || "— any —"}</option>)}
            </select>
          </div>

          <div>
            <label className="label-mono text-bm-dim">Geography inspiration</label>
            <select value={form.geography_inspiration} onChange={(e) => set("geography_inspiration", e.target.value)}
              data-testid="concept-geo" className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none">
              {CONCEPT_GEOGRAPHIES.map(g => <option key={g || "any"} value={g}>{g || "— any —"}</option>)}
            </select>
          </div>

          <div>
            <label className="label-mono text-bm-dim">Target archetype</label>
            <select value={form.target_archetype} onChange={(e) => set("target_archetype", e.target.value)}
              data-testid="concept-archetype" className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none">
              {CONCEPT_ARCHETYPES.map(a => <option key={a || "any"} value={a}>{a || "— any —"}</option>)}
            </select>
          </div>

          <div>
            <label className="label-mono text-bm-dim">Free-form prompt (optional)</label>
            <textarea value={form.free_prompt} onChange={(e) => set("free_prompt", e.target.value)}
              data-testid="concept-prompt"
              placeholder="e.g. 'walking-pad-friendly hydration for office desk'"
              rows={3}
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none focus:border-bm-cyan resize-none" />
          </div>

          <button onClick={generate} disabled={streaming} data-testid="concept-generate-btn"
            className="w-full bg-bm-amber text-black font-heading font-bold py-3 hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {streaming ? <><Loader2 className="w-4 h-4 animate-spin" />COMPOSING MEMO...</> : <><Sparkles className="w-4 h-4" />GENERATE CONCEPT</>}
          </button>
        </div>

        {/* Output */}
        <div className="bm-card lg:col-span-2 flex flex-col min-h-[500px] overflow-hidden" data-testid="concept-output">
          <div className="flex items-center justify-between flex-wrap gap-2 px-5 py-3 border-b border-white/5">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-bm-amber" />
              <span className="label-mono text-bm-amber">CONCEPT MEMO · claude-fable-5 + gpt-image</span>
            </div>
            <div className="flex items-center gap-2">
              {streaming && <span className="label-mono text-bm-cyan flex items-center gap-1"><span className="w-1.5 h-1.5 bg-bm-cyan animate-pulse"></span>GENERATING</span>}
              {activeConceptId && !streaming && legacyText && !memo && (
                <button onClick={rebuildAsMemo} disabled={memoizing} data-testid="rebuild-memo-btn"
                  className="flex items-center gap-2 px-3 h-9 text-xs font-mono bg-bm-cyan text-black hover:bg-white transition-colors disabled:opacity-50">
                  {memoizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  REBUILD AS MEMO
                </button>
              )}
              {activeConceptId && !streaming && !promoteResult?.opportunity_id && (
                <button onClick={promote} disabled={promoting} data-testid="promote-concept-btn"
                  className="flex items-center gap-2 px-3 h-9 text-xs font-mono bg-bm-green text-black hover:bg-white transition-colors disabled:opacity-50">
                  {promoting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                  ONE-CLICK PROMOTE TO OPPORTUNITY
                </button>
              )}
            </div>
          </div>

          {!hasOutput && (
            <div className="font-mono text-sm text-bm-muted leading-relaxed p-5">
              Set inputs at left and press <span className="text-bm-amber">GENERATE CONCEPT</span>. Claude composes a one-page infographic memo — packshot rendered by gpt-image after composition. Click <span className="text-bm-green">ONE-CLICK PROMOTE</span> to convert into a draft Opportunity.
            </div>
          )}

          {streaming && <div className="px-5"><MemoProgress chars={chars} /></div>}

          {!streaming && memo && (
            <div className="flex-1 overflow-auto">
              <ConceptMemo memo={memo} packshotUrl={packshotUrl} packshotLoading={packshotLoading} createdAt={memoCreatedAt} />
            </div>
          )}

          {!streaming && !memo && !legacyText && activeConceptId && (
            <div className="font-mono text-sm text-bm-dim p-5" data-testid="concept-empty-state">
              No concept content stored for this entry — nothing to display.
            </div>
          )}

          {!streaming && !memo && legacyText && (
            <div className="p-5 flex-1 overflow-auto">
              <div className="flex items-center gap-2 font-mono text-[10px] text-bm-dim mb-3">
                <FileText className="w-3 h-3" />LEGACY TEXT CONCEPT — rebuild as memo to get the infographic view
              </div>
              <div className="font-mono text-sm whitespace-pre-wrap text-white/90 leading-relaxed" data-testid="concept-text-output">
                {legacyText}
              </div>
            </div>
          )}

          {errorMsg && (
            <div className="px-5 py-3 border-t border-bm-danger/30 text-xs font-mono text-bm-danger" data-testid="concept-error">
              {errorMsg}
            </div>
          )}

          {promoteResult && promoteResult.opportunity_id && (
            <div className="border-t border-bm-green/30 bg-bm-green/5 px-5 py-3" data-testid="promote-result">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-bm-green shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-white font-mono">
                    {promoteResult.already_promoted ? "Already promoted to opportunity" : "Promoted to draft Opportunity with conviction scoring"}
                  </div>
                  <button onClick={() => navigate(`/opportunities/${promoteResult.opportunity_id}`)}
                    data-testid="view-promoted-opp-btn"
                    className="text-xs font-mono text-bm-green hover:text-white underline mt-1">
                    OPEN OPPORTUNITY MEMO →
                  </button>
                </div>
              </div>
            </div>
          )}

          {promoteResult && promoteResult.error && (
            <div className="px-5 py-3 border-t border-bm-danger/30 text-xs font-mono text-bm-danger" data-testid="promote-error">
              {promoteResult.error}
            </div>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div className="bm-card p-4" data-testid="concept-history">
          <div className="label-mono text-bm-cyan mb-3 flex items-center gap-2"><Clock className="w-3 h-3" />RECENT CONCEPTS · {history.length}</div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {history.slice(0, 20).map((c) => (
              <button key={c.id} onClick={() => loadHistory(c)}
                data-testid={`history-${c.id}`}
                className={`w-full text-left p-2 hover:bg-white/[0.04] border ${activeConceptId === c.id ? "border-bm-amber/50" : "border-transparent"} hover:border-white/10 flex items-start gap-2`}>
                {c.memo
                  ? <FileText className="w-3 h-3 text-bm-cyan mt-1 shrink-0" />
                  : <Sparkles className="w-3 h-3 text-bm-amber mt-1 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono text-bm-muted flex items-center gap-2 flex-wrap">
                    {new Date(c.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                    {c.project_name && <span className="text-bm-cyan"> · {c.project_name}</span>}
                    {c.raw_request?.category && <span className="text-bm-dim"> · {c.raw_request.category}</span>}
                    {c.memo && <span className="text-bm-cyan">MEMO</span>}
                    {c.promoted_opportunity_id && (
                      <span className="text-bm-green flex items-center gap-1">
                        <Rocket className="w-3 h-3" />PROMOTED
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-white truncate mt-0.5">
                    {c.memo ? `${c.memo.brand} — ${c.memo.product_name} · "${c.memo.one_line_pitch}"` : (c.concept_text || "").slice(0, 140) || "—"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
